/**
 * OptionRow — the product's single-select / multi-select option row.
 *
 * The glyph follows the control's semantics, so the affordance is never
 * ambiguous:
 *   radio    -> a circle that fills blossom with a WHITE centre dot
 *   checkbox -> an 18px rounded square that fills blossom with a WHITE check
 *
 * Both replace the browser's own rendering: with `accent-color` set to a light
 * brand yellow, Chrome and Edge pick a near-black mark for contrast, which is
 * the look this component exists to avoid. The white mark and the blossom fill
 * come from the Close Referral drawer's option cards
 * (features/expertises/community-corner/referrals/CloseReferralDrawer.tsx) —
 * only its square-and-check glyph is reserved for real checkboxes.
 *
 * The real <input> is kept (visually hidden via `sr-only`, still focusable and
 * still exposed as role="radio"/"checkbox"), so keyboard behaviour, form
 * semantics and every existing test that reaches for
 * getAllByRole("radio") / getByTestId(...).querySelector("input") keep working.
 * `peer` is only used for the focus ring; the fill is driven by the controlled
 * `checked` prop, exactly as the referral drawer drives it from `selected`.
 *
 * Rendered by BOTH ora and the Outlook add-in, so this file must stay free of
 * app-specific imports (no Tailwind config, no route constants).
 */
import type { ReactNode } from "react";

export type OptionType = "radio" | "checkbox";

/** 12px white check for checkboxes, matching CloseReferralDrawer's icon. */
const OptionCheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2.5 6.2 5 8.7 9.5 3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The indicator on its own, for callers that already own their <label> (a
 * nested <label> would be invalid HTML and would double-fire the control).
 * Render it as the NEXT SIBLING of an `sr-only peer` input so the focus ring
 * lands on it.
 */
export function OptionIndicator({
  checked,
  type = "radio",
}: {
  checked: boolean;
  type?: OptionType;
}) {
  const isRadio = type === "radio";
  return (
    <span
      aria-hidden="true"
      className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center border-[1.5px] text-base-white peer-focus-visible:ring-2 peer-focus-visible:ring-blossom ${
        isRadio ? "rounded-full" : "rounded"
      } ${
        checked
          ? "border-blossom bg-blossom"
          : "border-primary-gray-light-300 bg-base-white"
      }`}
    >
      {checked &&
        (isRadio ? (
          <span className="h-2 w-2 rounded-full bg-base-white" />
        ) : (
          <OptionCheckIcon />
        ))}
    </span>
  );
}

export interface OptionRowProps {
  /** Defaults to "radio"; pass "checkbox" for multi-select / boolean fields. */
  type?: OptionType;
  /** Required for radios so the browser groups them. */
  name?: string;
  value?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  id?: string;
  /** data-testid for the wrapping <label>. */
  testId?: string;
  /** data-testid for the <input> — some callers' tests click the input itself. */
  inputTestId?: string;
  /** Extra classes for the <label> (e.g. inline layout in a horizontal group). */
  className?: string;
  /** Label content. */
  children: ReactNode;
}

export function OptionRow({
  type = "radio",
  name,
  value,
  checked,
  onChange,
  disabled,
  id,
  testId,
  inputTestId,
  className = "",
  children,
}: OptionRowProps) {
  // `relative` makes this label the sr-only input's containing block. Without
  // it the input is positioned against the Outlook pane's Drawer paper (fixed,
  // overflow hidden) and does not scroll with the row, so focusing it from a
  // row below the fold scrolls the paper and blanks the pane (dev 0e845aa9).
  return (
    <label
      htmlFor={id}
      data-testid={testId}
      className={`relative flex items-start gap-2 ${
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
      } ${className}`}
    >
      <input
        id={id}
        type={type}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        data-testid={inputTestId}
        className="peer sr-only"
      />
      <OptionIndicator checked={checked} type={type} />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}

export default OptionRow;
